import React from "react";
import "./Charts.css";

const WIDTH = 320;
const HEIGHT = 140;
const PAD = { top: 8, right: 6, bottom: 22, left: 28 };
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

/**
 * Compact area or bar chart for an analytics tile: recessive gridlines, a
 * value axis and evenly spaced date labels. The figure above it carries the
 * number, so there are no data labels on the marks.
 */
export default function MiniChart({ values, labels, ticks, color, type = "area" }) {
  const max = ticks[ticks.length - 1];
  const y = (value) => PAD.top + PLOT_H - (value / max) * PLOT_H;
  const gradientId = `mini-${color.replace("#", "")}`;

  const x = (index, count) =>
    PAD.left + (count === 1 ? PLOT_W / 2 : (index / (count - 1)) * PLOT_W);

  const line = values
    .map((value, index) => `${index === 0 ? "M" : "L"}${x(index, values.length)},${y(value)}`)
    .join(" ");

  // Bars sit in equal slots rather than on the point grid.
  const slot = PLOT_W / values.length;
  const barWidth = Math.min(slot * 0.5, 22);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="mini"
      preserveAspectRatio="none"
      role="img"
      aria-label={`${type === "bar" ? "Bar" : "Trend"} chart, ${
        labels[0]
      } to ${labels[labels.length - 1]}`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0.01" />
        </linearGradient>
      </defs>

      {ticks.map((tick) => (
        <g key={tick}>
          <line
            x1={PAD.left}
            x2={PAD.left + PLOT_W}
            y1={y(tick)}
            y2={y(tick)}
            className="trend__grid"
          />
          <text x={PAD.left - 6} y={y(tick)} className="mini__tick mini__tick--y">
            {tick}
          </text>
        </g>
      ))}

      {type === "bar" ? (
        values.map((value, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <rect
            key={index}
            x={PAD.left + slot * index + (slot - barWidth) / 2}
            y={y(value)}
            width={barWidth}
            height={Math.max(PAD.top + PLOT_H - y(value), 2)}
            rx="3"
            fill={color}
          />
        ))
      ) : (
        <>
          <path
            d={`${line} L${x(values.length - 1, values.length)},${
              PAD.top + PLOT_H
            } L${x(0, values.length)},${PAD.top + PLOT_H} Z`}
            fill={`url(#${gradientId})`}
          />
          <path
            d={line}
            fill="none"
            stroke={color}
            strokeWidth="1.8"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          {values.map((value, index) => (
            <circle
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              cx={x(index, values.length)}
              cy={y(value)}
              r="2.4"
              fill={color}
            />
          ))}
        </>
      )}

      {labels.map((label, index) => (
        <text
          key={label}
          x={x(index, labels.length)}
          y={HEIGHT - 6}
          className="mini__tick"
          /* End labels anchor inward so they are not clipped by the viewBox. */
          textAnchor={
            index === 0
              ? "start"
              : index === labels.length - 1
                ? "end"
                : "middle"
          }
        >
          {label}
        </text>
      ))}
    </svg>
  );
}
